import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Coins, CreditCard, Sparkle, ArrowRight, CurrencyBtc, ShieldCheck, Lightning, Bank, Copy, Check, Clock, XCircle, CheckCircle } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const TokensPage = () => {
  const { user, refreshUser } = useAuth();
  const [packages, setPackages] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [myClaims, setMyClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('zelle');
  const [showPaymentModal, setShowPaymentModal] = useState(null);
  const [reference, setReference] = useState('');
  const [senderName, setSenderName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [packagesRes, transactionsRes, claimsRes] = await Promise.all([
        axios.get(`${API}/api/tokens/packages`),
        axios.get(`${API}/api/tokens/transactions`),
        axios.get(`${API}/api/payments/claims/my`),
      ]);
      setPackages(packagesRes.data);
      setTransactions(transactionsRes.data);
      setMyClaims(claimsRes.data);
    } catch (err) {
      console.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const handleDirectPurchase = (packageId) => {
    setShowPaymentModal(packageId);
    setReference('');
    setSenderName('');
  };

  const handleSubmitClaim = async () => {
    if (!reference.trim()) {
      toast.error('Please enter a payment reference or confirmation number');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/payments/claim`, {
        package_id: showPaymentModal,
        method: paymentMethod,
        reference: reference.trim(),
        sender_name: senderName.trim(),
      });
      toast.success('Payment claim submitted! Tokens will be credited after verification.');
      setShowPaymentModal(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit claim');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCryptoPurchase = async (packageId) => {
    setPurchasing(packageId);
    try {
      const response = await axios.post(`${API}/api/tokens/purchase`, {
        package_id: packageId,
        origin_url: window.location.origin,
        payment_method: 'crypto',
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error('Payment URL not available.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate purchase');
    } finally {
      setPurchasing(null);
    }
  };

  const selectedPkg = packages.find(p => p.id === showPaymentModal);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 pb-24" data-testid="tokens-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-white mb-2">Tokens</h1>
        <p className="text-slate-400">Purchase tokens to unlock exclusive content</p>
      </div>

      {/* Balance Card */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-amber-500/20 via-amber-600/10 to-amber-700/5 border border-amber-500/20 p-6 mb-8">
        <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="relative">
          <p className="text-amber-400/70 text-sm font-medium mb-1">Your Balance</p>
          <div className="flex items-center gap-3">
            <Coins weight="duotone" className="w-10 h-10 text-amber-400" />
            <span className="font-heading text-5xl font-black text-white" data-testid="token-balance">{user?.tokens || 0}</span>
            <span className="text-2xl text-slate-400 font-medium">tokens</span>
          </div>
        </div>
      </div>

      {/* Payment Method Toggle */}
      <div className="mb-6">
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-3">Payment Method</h2>
        <div className="grid grid-cols-3 gap-3">
          {[
            { id: 'zelle', icon: Bank, label: 'Zelle', sub: 'US instant transfer', color: 'purple' },
            { id: 'bank_transfer_mx', icon: Bank, label: 'Bank Transfer', sub: 'Mexico (CLABE)', color: 'emerald' },
            { id: 'crypto', icon: CurrencyBtc, label: 'Crypto', sub: 'BTC, ETH, USDT +350', color: 'orange' },
          ].map(m => (
            <button
              key={m.id}
              onClick={() => setPaymentMethod(m.id)}
              className={`flex items-center gap-3 p-4 rounded-2xl border transition-all ${
                paymentMethod === m.id
                  ? `bg-${m.color}-500/10 border-${m.color}-500/30 text-white`
                  : 'bg-slate-900/50 border-white/5 text-slate-500 hover:text-slate-300 hover:border-white/10'
              }`}
              data-testid={`payment-method-${m.id}`}
            >
              <m.icon weight="duotone" className={`w-6 h-6 ${paymentMethod === m.id ? `text-${m.color}-400` : ''}`} />
              <div className="text-left">
                <span className="block font-semibold text-sm">{m.label}</span>
                <span className="block text-xs text-slate-500">{m.sub}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Packages */}
      <div className="mb-8">
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">Buy Tokens</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {packages.map((pkg, index) => {
            const isPopular = pkg.id === 'popular';
            const isBest = pkg.id === 'value';
            return (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`relative rounded-2xl p-5 border transition-all hover:scale-105 ${
                  isPopular ? 'bg-blue-500/10 border-blue-500/30' : isBest ? 'bg-amber-500/10 border-amber-500/30' : 'bg-slate-900/50 border-white/5'
                }`}
                data-testid={`token-package-${pkg.id}`}
              >
                {isPopular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-blue-500 text-white text-xs font-bold rounded-full">Popular</span>}
                {isBest && <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-amber-500 text-amber-950 text-xs font-bold rounded-full flex items-center gap-1"><Sparkle weight="fill" className="w-3 h-3" />Best Value</span>}
                <div className="text-center mb-4">
                  <Coins weight="duotone" className={`w-12 h-12 mx-auto mb-2 ${isPopular ? 'text-blue-400' : isBest ? 'text-amber-400' : 'text-slate-400'}`} />
                  <div className="font-heading text-3xl font-bold text-white">{pkg.tokens}</div>
                  <div className="text-slate-500 text-sm">tokens</div>
                </div>
                <div className="text-center mb-4">
                  <span className="text-2xl font-bold text-white">${pkg.price}</span>
                </div>
                <button
                  onClick={() => paymentMethod === 'crypto' ? handleCryptoPurchase(pkg.id) : handleDirectPurchase(pkg.id)}
                  disabled={purchasing === pkg.id}
                  className={`w-full py-3 rounded-full font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2 ${
                    isPopular ? 'bg-blue-500 text-white hover:bg-blue-600' : isBest ? 'bg-amber-500 text-amber-950 hover:bg-amber-600' : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                  data-testid={`buy-tokens-${pkg.id}`}
                >
                  {purchasing === pkg.id ? (
                    <span className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>Buy Now</>
                  )}
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Pending Claims */}
      {myClaims.length > 0 && (
        <div className="mb-8">
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">Payment Claims</h2>
          <div className="space-y-2">
            {myClaims.map(c => (
              <div key={c.claim_id} className="flex items-center gap-4 p-4 bg-slate-900/50 border border-white/5 rounded-xl">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  c.status === 'approved' ? 'bg-green-500/20' : c.status === 'rejected' ? 'bg-red-500/20' : 'bg-amber-500/20'
                }`}>
                  {c.status === 'approved' ? <CheckCircle className="w-5 h-5 text-green-400" /> :
                   c.status === 'rejected' ? <XCircle className="w-5 h-5 text-red-400" /> :
                   <Clock className="w-5 h-5 text-amber-400" />}
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium">{c.tokens} tokens via {c.method}</p>
                  <p className="text-xs text-slate-500">Ref: {c.reference} | {new Date(c.created_at).toLocaleDateString()}</p>
                </div>
                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                  c.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                  c.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                  'bg-amber-500/20 text-amber-400'
                }`}>{c.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Transaction History */}
      <div>
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">Transaction History</h2>
        {transactions.length === 0 ? (
          <div className="text-center py-12 bg-slate-900/50 border border-white/5 rounded-2xl">
            <Coins className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-500">No transactions yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {transactions.map(tx => (
              <div key={tx.id} className="flex items-center gap-4 p-4 bg-slate-900/50 border border-white/5 rounded-xl">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${tx.amount > 0 ? 'bg-green-500/20' : 'bg-blue-500/20'}`}>
                  <ArrowRight className={`w-5 h-5 ${tx.amount > 0 ? 'text-green-400 rotate-180' : 'text-blue-400'}`} />
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium">{tx.description}</p>
                  <p className="text-sm text-slate-500">{new Date(tx.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                </div>
                <div className={`font-semibold ${tx.amount > 0 ? 'text-green-400' : 'text-blue-400'}`}>
                  {tx.amount > 0 ? '+' : ''}{tx.amount}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Payment Instructions Modal */}
      <AnimatePresence>
        {showPaymentModal && selectedPkg && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={() => setShowPaymentModal(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-900 border border-white/10 rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto overscroll-contain"
              onClick={e => e.stopPropagation()}
              data-testid="payment-modal"
            >
              <h3 className="text-xl font-bold text-white mb-1">
                Buy {selectedPkg.tokens} Tokens — ${selectedPkg.price}
              </h3>
              <p className="text-slate-400 text-sm mb-6">
                {paymentMethod === 'zelle' ? 'Send via Zelle, then confirm below' : 'Transfer via bank, then confirm below'}
              </p>

              {paymentMethod === 'zelle' ? (
                <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-xl p-4 mb-6">
                  <p className="text-indigo-300 text-sm font-medium mb-2">Send ${selectedPkg.price} via Zelle to:</p>
                  <div className="flex items-center justify-between bg-black/30 rounded-lg px-3 py-2">
                    <span className="text-white font-mono">gooseco23@gmail.com</span>
                    <button onClick={() => copyToClipboard('gooseco23@gmail.com', 'zelle')} className="text-indigo-400 hover:text-indigo-300">
                      {copied === 'zelle' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-indigo-400/60 text-xs mt-2">Include your LustLink username in the memo</p>
                </div>
              ) : (
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 mb-6">
                  <p className="text-emerald-300 text-sm font-medium mb-2">Transfer ${selectedPkg.price} USD to:</p>
                  <div className="space-y-2">
                    <div>
                      <span className="text-xs text-emerald-400/60">Account</span>
                      <div className="flex items-center justify-between bg-black/30 rounded-lg px-3 py-2">
                        <span className="text-white font-mono text-sm">00000712059</span>
                        <button onClick={() => copyToClipboard('00000712059', 'acct')} className="text-emerald-400 hover:text-emerald-300">
                          {copied === 'acct' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-emerald-400/60">CLABE</span>
                      <div className="flex items-center justify-between bg-black/30 rounded-lg px-3 py-2">
                        <span className="text-white font-mono text-sm">113180000007120596</span>
                        <button onClick={() => copyToClipboard('113180000007120596', 'clabe')} className="text-emerald-400 hover:text-emerald-300">
                          {copied === 'clabe' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <p className="text-emerald-400/60 text-xs mt-2">Include your LustLink username in the reference</p>
                </div>
              )}

              <div className="space-y-3 mb-6">
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Confirmation / Reference Number *</label>
                  <input
                    type="text"
                    value={reference}
                    onChange={e => setReference(e.target.value)}
                    placeholder="Enter your payment confirmation number"
                    className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-600 focus:ring-2 focus:ring-blue-500/50"
                    data-testid="payment-reference-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Sender Name (optional)</label>
                  <input
                    type="text"
                    value={senderName}
                    onChange={e => setSenderName(e.target.value)}
                    placeholder="Name on the payment"
                    className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-600 focus:ring-2 focus:ring-blue-500/50"
                    data-testid="payment-sender-input"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowPaymentModal(null)}
                  className="flex-1 py-3 rounded-full border border-white/10 text-slate-400 hover:text-white hover:border-white/20 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitClaim}
                  disabled={submitting || !reference.trim()}
                  className="flex-1 py-3 rounded-full bg-blue-500 text-white font-semibold hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="submit-payment-claim-btn"
                >
                  {submitting ? (
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <ShieldCheck className="w-5 h-5" />
                      I've Sent Payment
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TokensPage;
